import type {
  CareerStatistics,
  CareerUI,
  EnrichedCareerEntity,
} from '@/domains/centerLayer/centerLayer.entity';

/**
 * Parámetros opcionales para procesamiento de CenterLayer
 */
export interface ProcessCenterLayerDataParams {
  area?: string | null;
}

/**
 * Datos procesados de CenterLayer listos para UI
 */
export interface ProcessedCenterLayerData {
  careersForUI: CareerUI[];
  statistics: CareerStatistics;
}

/**
 * Use Case para procesar datos de carreras de CenterLayer
 * Cumple SRP: Solo se encarga de transformar datos y calcular estadísticas
 * Cumple OCP: Abierto a extensión (nuevos filtros) cerrado a modificación
 *
 * Responsabilidades:
 * - Filtrar carreras por área cuando es requerido
 * - Transformar carreras de entidad a formato UI
 * - Calcular porcentajes de riesgo por carrera
 * - Calcular estadísticas agregadas (totales, porcentajes)
 * - Validar integridad de datos de entrada
 */
export class ProcessCenterLayerDataUseCase {
  /**
   * Filtra carreras por nombre de área específica
   * @param careers - Array de carreras a filtrar
   * @param areaName - Nombre exacto del área a filtrar
   * @returns Array filtrado de carreras que pertenecen al área
   */
  private filterByArea(
    careers: EnrichedCareerEntity[],
    areaName: string,
  ): EnrichedCareerEntity[] {
    return careers.filter((career) => career.area === areaName);
  }

  /**
   * Transforma una carrera de entidad de dominio a formato UI
   * Calcula el porcentaje de riesgo de la carrera
   * @param career - Carrera en formato de entidad de dominio
   * @returns Carrera transformada con porcentaje de riesgo calculado
   */
  private transformToUI(career: EnrichedCareerEntity): CareerUI {
    const totalEstudiantes = career['total-estudiante'];
    const estudiantesConRiesgo = career['estudiantes-con-riesgo'];

    const porcentajeRiesgo =
      estudiantesConRiesgo > 0 && totalEstudiantes > 0
        ? Math.round((estudiantesConRiesgo / totalEstudiantes) * 100)
        : 0;

    return {
      nombre: career.nombre,
      cod_carrera: career.cod_carrera,
      cod_compuesto: career.cod_compuesto,
      'nombre-coordinador': career['nombre-coordinador'] ?? 'Sin asignar',
      jornada: career.jornada,
      totalEstudiantes,
      estudiantesConRiesgo,
      porcentajeRiesgo,
    };
  }

  /**
   * Calcula estadísticas agregadas de todas las carreras
   * Incluye totales de carreras, estudiantes, estudiantes en riesgo y porcentaje general
   * @param careers - Array de carreras en formato UI
   * @returns Objeto con estadísticas calculadas
   */
  private calculateStatistics(careers: CareerUI[]): CareerStatistics {
    const totalCarreras = careers.length;
    const totalEstudiantes = careers.reduce(
      (sum, career) => sum + career.totalEstudiantes,
      0,
    );
    const totalEstudiantesConRiesgo = careers.reduce(
      (sum, career) => sum + career.estudiantesConRiesgo,
      0,
    );

    const porcentajeRiesgoGeneral =
      totalEstudiantes > 0
        ? Math.round(
            (totalEstudiantesConRiesgo / totalEstudiantes) * 100 * 100,
          ) / 100
        : 0;

    return {
      totalCarreras,
      totalEstudiantes,
      totalEstudiantesConRiesgo,
      porcentajeRiesgoGeneral,
    };
  }

  /**
   * Retorna estadísticas por defecto cuando no hay datos
   * @returns Objeto de estadísticas con todos los valores en cero
   */
  private getDefaultStatistics(): CareerStatistics {
    return {
      totalCarreras: 0,
      totalEstudiantes: 0,
      totalEstudiantesConRiesgo: 0,
      porcentajeRiesgoGeneral: 0,
    };
  }

  /**
   * Ejecuta el procesamiento completo de carreras de CenterLayer
   * Flujo: 1) Filtrar por área (opcional), 2) Transformar a UI, 3) Calcular estadísticas
   * @param careers - Lista de carreras enriquecidas desde la entidad
   * @param params - Parámetros opcionales (área para filtrar)
   * @returns Objeto con carreras transformadas y estadísticas calculadas
   */
  execute(
    careers: EnrichedCareerEntity[],
    params: ProcessCenterLayerDataParams = {},
  ): ProcessedCenterLayerData {
    if (!careers.length) {
      return {
        careersForUI: [],
        statistics: this.getDefaultStatistics(),
      };
    }

    const filteredCareers = params.area
      ? this.filterByArea(careers, params.area)
      : careers;

    const careersForUI = filteredCareers.map((career) =>
      this.transformToUI(career),
    );

    const statistics = this.calculateStatistics(careersForUI);

    return { careersForUI, statistics };
  }
}
